/**
 * 统一请求实例（axios）。
 * - baseURL 留空，走 vite proxy `/api`（见 vite.config.ts）。
 * - 请求拦截器注入 Authorization: Bearer <token>（localStorage['bluplanet_token']）。
 * - 响应拦截器统一解析 Result{code,message,data}：code===200 取 data；否则以 message 抛错。
 */

import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

export const TOKEN_KEY = 'bluplanet_token';

const instance = axios.create({
  // 统一走 vite proxy '/api'（见 vite.config.ts server.proxy['/api'] -> gateway:8080）
  baseURL: '/api',
  timeout: 15000,
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  // 注入当前用户 ID（从 localStorage 获取）
  const userId = localStorage.getItem('bluplanet_user_id');
  if (userId) {
    (config.headers as Record<string, string>)['X-User-Id'] = userId;
  }
  return config;
});

instance.interceptors.response.use(
  (response) => {
    const body = response.data;
    // 后端统一包装为 Result{code,message,data}
    if (body && typeof body.code === 'number') {
      if (body.code !== 200) {
        return Promise.reject(new Error(body.message || '请求失败'));
      }
      // 直接返回 data 负载，调用方无需再 .data
      return body.data;
    }
    return body;
  },
  (error) => {
    const status = error?.response?.status;
    const message =
      error?.response?.data?.message || error?.message || '网络错误，请稍后重试';
    if (status === 401) {
      localStorage.removeItem(TOKEN_KEY);
    }
    // 将 HTTP 状态码挂在错误对象上，便于调用方（如履约节点 403）做精准文案分支
    const rejected = new Error(message) as Error & { status?: number };
    rejected.status = status;
    return Promise.reject(rejected);
  }
);

/** 泛型请求：自动解析 Result<T>.data 为 T */
export async function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  return instance(config) as unknown as Promise<T>;
}

export default request;
